package lacaravana.sync

import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import lacaravana.app.Database
import lacaravana.app.appScope
import kotlin.js.Promise

@JsModule("nats.ws")
@JsNonModule
external object NatsWs {
    val connect: dynamic
    val JSONCodec: dynamic
}

object NatsSync {
    private var db: Database? = null
    private var nc: dynamic = null
    private var jc: dynamic = null
    private var mySiteId: String = ""
    private var lastVersion: Long = 0
    private var applyingChanges = false
    var connected = false
        private set

    suspend fun start(database: Database, natsUrl: String? = null) {
        db = database
        mySiteId = database.siteId()
        lastVersion = database.dbVersion()
        jc = NatsWs.JSONCodec()
        val url = natsUrl ?: defaultNatsUrl()
        connectNats(url)
    }

    private fun defaultNatsUrl(): String {
        val params = js("new URLSearchParams(window.location.search)")
        val explicit = params.get("nats") as? String
        if (explicit != null) return explicit
        val loc = js("window.location")
        val wsProto = if ((loc.protocol as String) == "https:") "wss:" else "ws:"
        return "$wsProto//${loc.host}/nats"
    }

    private fun connectNats(url: String) {
        appScope.launch {
            try {
                val opts = js("{}")
                opts.servers = url
                opts.reconnect = true
                opts.maxReconnectAttempts = -1
                opts.reconnectTimeWait = 3000
                nc = (NatsWs.connect.unsafeCast<(dynamic) -> Promise<dynamic>>()(opts)).await()
                connected = true
                subscribeToChanges()
                pushChanges()
            } catch (e: dynamic) {
                connected = false
                delay(3000)
                connectNats(url)
            }
        }
    }

    private fun subscribeToChanges() {
        val opts = js("{}")
        opts.callback = fun(err: dynamic, msg: dynamic) {
            if (err != null) return
            val subject = msg.subject as String
            val senderSiteId = subject.substringAfterLast(".")
            if (senderSiteId == mySiteId) return
            try {
                val payload = jc.decode(msg.data)
                appScope.launch { receiveChanges(payload) }
            } catch (_: dynamic) {}
        }
        nc.subscribe("lacaravana.sync.>", opts)
    }

    suspend fun pushChanges() {
        if (applyingChanges) return
        val database = db ?: return
        if (nc == null || !connected) return
        val currentVersion = database.dbVersion()
        if (currentVersion <= lastVersion) return
        val changes = database.changesSince(lastVersion)
        if (changes.isEmpty()) return
        lastVersion = currentVersion

        val payload = changes.map { row ->
            row.map { cell ->
                if (cell != null && js("cell instanceof Uint8Array").unsafeCast<Boolean>()) {
                    val arr = cell.unsafeCast<dynamic>()
                    val hex = js("Array.from(arr, function(b) { return b.toString(16).padStart(2, '0') }).join('')")
                    val wrapper = js("{}")
                    wrapper.__bytes__ = hex
                    wrapper
                } else {
                    cell ?: js("null")
                }
            }.toTypedArray()
        }.toTypedArray()

        try {
            nc.publish("lacaravana.sync.$mySiteId", jc.encode(payload))
        } catch (_: dynamic) {}
    }

    private suspend fun receiveChanges(payload: dynamic) {
        val database = db ?: return
        val changesArray = payload.unsafeCast<Array<Array<Any?>>>()
        val changes = changesArray.map { row ->
            row.map { cell ->
                if (cell != null && js("cell.__bytes__ !== undefined").unsafeCast<Boolean>()) {
                    val hex = cell.asDynamic().__bytes__ as String
                    js("new Uint8Array(hex.match(/.{2}/g).map(function(b) { return parseInt(b, 16) }))")
                } else {
                    cell
                }
            }
        }
        applyingChanges = true
        try {
            database.applyChanges(changes)
            lastVersion = database.dbVersion()
        } finally {
            applyingChanges = false
        }
    }

    fun stop() {
        appScope.launch {
            try { (nc?.close() as? Promise<dynamic>)?.await() } catch (_: dynamic) {}
        }
        nc = null
        connected = false
    }
}
